import { useEffect, useState } from 'react'
import { Alert, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { WarningAmber } from '@mui/icons-material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * S119-S122 (CU6/RN44, historia #100) — the dialog a cancel/close of a fabrication order opens when
 * Sun answers "requires confirmation" (E-M13) because one or more external jobs are still dispatched.
 * Lists every dispatched job and asks for a per-job OK/NOK verdict; POL3 is an explicit exception to
 * RN19: the forced return date is the instant of the act, never editable, so this dialog never shows
 * a date field.
 *
 * Props:
 *  - open: whether the dialog is visible.
 *  - jobs: [{ jobReferenceId, label }] — the pending jobs snapshot (arbitrated contract,
 *    `error.data.pendingJobs`). `label` is rendered VERBATIM (POL4), never translated or split.
 *  - changed: true when this open is a RE-OPEN after the server rejected a stale decision set
 *    (RN44.2/V15) — shows a "list has changed" banner instead of silently swapping the rows.
 *  - submitting: disables every control while the confirm request is in flight.
 *  - onConfirm(decisions): decisions is { [jobReferenceId]: 'OK' | 'NOK' }, only reachable once every
 *    job has a verdict (RN44/CU6-A1: no default, no partial submission).
 *  - onCancel(): closing or cancelling the dialog — nothing is sent (S120).
 */
const ForcedReturnDialog = ({ open, jobs = [], changed = false, submitting = false, onConfirm = () => {}, onCancel = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [choices, setChoices] = useState({})

  // S120: every (re)open — including the RN44.2 re-open with a fresh job set — starts with no
  // verdict preselected, and stale choices from a previous set never carry over.
  useEffect(() => {
    if (open) setChoices({})
  }, [open, jobs])

  const setChoice = (jobReferenceId, verdict) => {
    setChoices((previous) => ({ ...previous, [jobReferenceId]: verdict }))
  }

  const allDecided = jobs.length > 0 && jobs.every((job) => !!choices[job.jobReferenceId])

  return (
    <SmartDialog
      setOpen={open}
      accept={false}
      cancel
      close={false}
      disableButtons={submitting}
      cancelCallback={onCancel}
      title={createDialogTitle({ color: 'warning', title: t_('Subcontracted work in progress'), icon: WarningAmber })}
      renderComponent={
        <Stack spacing={2} sx={{ padding: '0.5em 1em', minWidth: '24rem' }}>
          <Typography>{t_('This fabrication order has external jobs still dispatched. Choose how to close them before continuing.')}</Typography>
          {changed
            ? <Alert severity='warning'>{t_('The list of dispatched jobs has changed. Please review it again.')}</Alert>
            : null}
          {jobs.map((job) => (
            <Stack key={job.jobReferenceId} direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
              <Typography>{job.label}</Typography>
              <ToggleButtonGroup
                size='small'
                exclusive
                disabled={submitting}
                value={choices[job.jobReferenceId] || ''}
                onChange={(event, verdict) => { if (verdict) setChoice(job.jobReferenceId, verdict) }}
              >
                <ToggleButton value='OK'>{t_('Return as OK')}</ToggleButton>
                <ToggleButton value='NOK'>{t_('Return as NOK')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          ))}
        </Stack>
      }
      renderCustomButtons={
        <Button color='primary' variant='outlined' disabled={!allDecided || submitting} onClick={() => onConfirm(choices)}>
          <Typography>{t_('Confirm')}</Typography>
        </Button>
      }
    />
  )
}

export default ForcedReturnDialog
